import { ENDPOINTS } from "@/api/application-api";
import { authHeader } from "@/utils/headers";

interface WorkspaceEntityTagInfo {
  id: string;
  entityType: string;
  name: string;
  shouldPreserveUrlMetadata?: boolean;
}

interface WorkspaceSmartTableRef {
  workspaceId: string;
  smartTableId: string;
}

interface WorkspaceToolRef {
  workspaceId: string;
  toolId: string;
}

interface ConvertWorkspaceUrlsToTagsOptions {
  promptNamesById?: Record<string, string>;
  smartTableNamesById?: Record<string, string>;
  toolNamesById?: Record<string, string>;
}

interface ConvertWorkspaceUrlsToTagsAsyncOptions extends ConvertWorkspaceUrlsToTagsOptions {
  userToken?: string | null;
  resolvePromptNames?: (promptIds: string[]) => Promise<Record<string, string>>;
  resolveSmartTableNames?: (
    smartTableRefs: WorkspaceSmartTableRef[],
  ) => Promise<Record<string, string>>;
  resolveToolNames?: (toolRefs: WorkspaceToolRef[]) => Promise<Record<string, string>>;
}

interface PromptRegistryNameResponse {
  prompt_name?: string;
}

interface SmartTableNameResponse {
  smart_table?: {
    title?: string | null;
  } | null;
  title?: string | null;
}

interface ToolRegistryNameResponse {
  tool_registry?: {
    name?: string | null;
  } | null;
  name?: string | null;
}

const TRACE_ID_SHORT_DISPLAY_LENGTH = 8;

enum WorkspaceEntityRouteType {
  FOLDER = "FOLDER",
  DATASET = "DATASET",
  REPORT = "REPORT",
  PROMPT = "PROMPT",
  WORKFLOW = "WORKFLOW",
  AB_TEST = "AB_TEST",
  SNIPPET = "SNIPPET",
  INPUT_VARIABLE_SET = "INPUT_VARIABLE_SET",
  SKILL_COLLECTION = "SKILL_COLLECTION",
  SMART_TABLE = "SMART_TABLE",
  TOOL = "TOOL",
  REQUEST = "REQUEST",
  TRACE = "TRACE",
  EVALUATE_COMPARE = "EVALUATE_COMPARE",
}

const WORKSPACE_URL_REGEX = /(?:https?:\/\/[^\s/]+)?\/?workspace\/\d+\/[^\s]+/gi;
const TAG_TOKEN_REGEX = /@@@tag:(.*?)@@@/g;

interface TagTokenRange {
  start: number;
  end: number;
}

const getTagTokenRanges = (message: string): TagTokenRange[] =>
  Array.from(message.matchAll(TAG_TOKEN_REGEX), match => ({
    start: match.index ?? 0,
    end: (match.index ?? 0) + match[0].length,
  }));

const isInsideTagToken = (index: number, tagTokenRanges: TagTokenRange[]) =>
  tagTokenRanges.some(({ start, end }) => index >= start && index < end);

const getSmartTableNameKey = (workspaceId: string, smartTableId: string) =>
  `${workspaceId}:${smartTableId}`;

const getToolNameKey = (workspaceId: string, toolId: string) => `${workspaceId}:${toolId}`;

const trimTrailingPunctuation = (value: string): { core: string; trailing: string } => {
  const trailingMatch = value.match(/[),.;!?]+$/);
  if (!trailingMatch) {
    return { core: value, trailing: "" };
  }

  const trailing = trailingMatch[0];
  return {
    core: value.slice(0, -trailing.length),
    trailing,
  };
};

const parseWorkspaceUrl = (
  rawMatch: string,
): { workspaceId: string; resourcePath: string } | null => {
  const { core } = trimTrailingPunctuation(rawMatch);
  if (!core) {
    return null;
  }

  const normalized = core.startsWith("workspace/") ? `/${core}` : core;
  const parsedUrl =
    normalized.startsWith("http://") || normalized.startsWith("https://")
      ? new URL(normalized)
      : new URL(normalized, "http://localhost");

  const workspaceMatch = parsedUrl.pathname.match(/^\/workspace\/(\d+)\/(.+)$/);
  if (!workspaceMatch) {
    return null;
  }

  return {
    workspaceId: workspaceMatch[1],
    resourcePath: workspaceMatch[2],
  };
};

const getWorkspaceEntityRefsFromWorkspaceUrls = (
  message: string,
): {
  promptIds: string[];
  smartTableRefs: WorkspaceSmartTableRef[];
  toolRefs: WorkspaceToolRef[];
} => {
  const tagTokenRanges = getTagTokenRanges(message);
  const promptIds = new Set<string>();
  const smartTableRefsByKey = new Map<string, WorkspaceSmartTableRef>();
  const toolRefsByKey = new Map<string, WorkspaceToolRef>();

  Array.from(message.matchAll(WORKSPACE_URL_REGEX)).forEach(match => {
    const rawMatch = match[0];
    const offset = match.index ?? 0;
    if (isInsideTagToken(offset, tagTokenRanges)) {
      return;
    }

    try {
      const parsedWorkspaceUrl = parseWorkspaceUrl(rawMatch);
      const promptMatch = parsedWorkspaceUrl?.resourcePath.match(/^prompt\/(\d+)(?:\/|$)/);
      if (promptMatch) {
        promptIds.add(promptMatch[1]);
      }
      const smartTableMatch = parsedWorkspaceUrl?.resourcePath.match(
        /^smart-tables\/([^/]+)(?:\/|$)/,
      );
      if (parsedWorkspaceUrl && smartTableMatch) {
        const ref = {
          workspaceId: parsedWorkspaceUrl.workspaceId,
          smartTableId: smartTableMatch[1],
        };
        smartTableRefsByKey.set(getSmartTableNameKey(ref.workspaceId, ref.smartTableId), ref);
      }
      const toolMatch = parsedWorkspaceUrl?.resourcePath.match(/^tool\/(\d+)(?:\/|$)/);
      if (parsedWorkspaceUrl && toolMatch) {
        const ref = {
          workspaceId: parsedWorkspaceUrl.workspaceId,
          toolId: toolMatch[1],
        };
        toolRefsByKey.set(getToolNameKey(ref.workspaceId, ref.toolId), ref);
      }
    } catch {
      // Ignore malformed URLs and leave them unchanged in the converter.
    }
  });

  return {
    promptIds: Array.from(promptIds),
    smartTableRefs: Array.from(smartTableRefsByKey.values()),
    toolRefs: Array.from(toolRefsByKey.values()),
  };
};

const fetchPromptNamesById = async (
  promptIds: string[],
  userToken?: string | null,
): Promise<Record<string, string>> => {
  if (!userToken || promptIds.length === 0) {
    return {};
  }

  const entries = await Promise.all(
    promptIds.map(async promptId => {
      const response = await fetch(`${ENDPOINTS.prompt_registry_objects}/${promptId}`, {
        headers: authHeader(userToken),
      });

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as PromptRegistryNameResponse;
      const promptName = data.prompt_name?.trim();
      return promptName ? ([promptId, promptName] as const) : null;
    }),
  );

  return Object.fromEntries(entries.filter(entry => entry !== null));
};

const fetchSmartTableNamesById = async (
  smartTableRefs: WorkspaceSmartTableRef[],
  userToken?: string | null,
): Promise<Record<string, string>> => {
  if (!userToken || smartTableRefs.length === 0) {
    return {};
  }

  const entries = await Promise.all(
    smartTableRefs.map(async ({ workspaceId, smartTableId }) => {
      const response = await fetch(ENDPOINTS.smart_table(Number(workspaceId), smartTableId), {
        headers: authHeader(userToken),
      });

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as SmartTableNameResponse;
      const tableTitle = (data.smart_table?.title ?? data.title)?.trim();
      return tableTitle
        ? ([getSmartTableNameKey(workspaceId, smartTableId), tableTitle] as const)
        : null;
    }),
  );

  return Object.fromEntries(entries.filter(entry => entry !== null));
};

const fetchToolNamesById = async (
  toolRefs: WorkspaceToolRef[],
  userToken?: string | null,
): Promise<Record<string, string>> => {
  if (!userToken || toolRefs.length === 0) {
    return {};
  }

  const entries = await Promise.all(
    toolRefs.map(async ({ workspaceId, toolId }) => {
      const response = await fetch(
        ENDPOINTS.tool_registry.get(Number(workspaceId), Number(toolId)),
        {
          headers: authHeader(userToken),
        },
      );

      if (!response.ok) {
        return null;
      }

      const data = (await response.json()) as ToolRegistryNameResponse;
      const toolName = (data.tool_registry?.name ?? data.name)?.trim();
      return toolName ? ([getToolNameKey(workspaceId, toolId), toolName] as const) : null;
    }),
  );

  return Object.fromEntries(entries.filter(entry => entry !== null));
};

const parseWorkspaceEntityTagInfo = (
  workspaceId: string,
  resourcePath: string,
  searchParams?: URLSearchParams,
  options: ConvertWorkspaceUrlsToTagsOptions = {},
): WorkspaceEntityTagInfo | null => {
  const matchers: Record<WorkspaceEntityRouteType, RegExpMatchArray | null> = {
    [WorkspaceEntityRouteType.FOLDER]: resourcePath.match(/^folder\/(\d+)(?:\/|$)/),
    [WorkspaceEntityRouteType.DATASET]: resourcePath.match(
      /^dataset-groups\/(\d+)(?:\/dataset\/(\d+))?(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.REPORT]: resourcePath.match(
      /^evaluate\/blueprints\/(\d+)(?:\/reports\/(\d+))?(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.PROMPT]: resourcePath.match(
      /^prompt\/(\d+)(?:\/version\/(\d+))?(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.WORKFLOW]: resourcePath.match(
      /^workflow\/(\d+)(?:\/version\/(\d+))?(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.AB_TEST]: resourcePath.match(
      /^ab-releases\/prompt-release-group\/(\d+)(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.SNIPPET]: resourcePath.match(/^snippet\/(\d+)(?:\/|$)/),
    [WorkspaceEntityRouteType.INPUT_VARIABLE_SET]: resourcePath.match(
      /^input-variable-set\/(\d+)(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.SKILL_COLLECTION]: resourcePath.match(
      /^skill-collection\/([^/]+)(?:\/version\/(\d+))?(?:\/|$)/,
    ),
    [WorkspaceEntityRouteType.SMART_TABLE]: resourcePath.match(/^smart-tables\/([^/]+)(?:\/|$)/),
    [WorkspaceEntityRouteType.TOOL]: resourcePath.match(/^tool\/(\d+)(?:\/|$)/),
    [WorkspaceEntityRouteType.REQUEST]: resourcePath.match(/^request\/(\d+)(?:\/|$)/),
    [WorkspaceEntityRouteType.TRACE]: resourcePath.match(/^traces\/([^/]+)(?:\/|$)/),
    [WorkspaceEntityRouteType.EVALUATE_COMPARE]: resourcePath.match(/^evaluate\/compare(?:\/|$)/),
  };

  const type = (Object.values(WorkspaceEntityRouteType) as WorkspaceEntityRouteType[]).find(
    entityType => Boolean(matchers[entityType]),
  );

  if (!type) {
    return null;
  }

  switch (type) {
    case WorkspaceEntityRouteType.FOLDER: {
      const match = matchers[WorkspaceEntityRouteType.FOLDER]!;
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.FOLDER,
        name: `Folder #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.DATASET: {
      const match = matchers[WorkspaceEntityRouteType.DATASET]!;
      const datasetId = match[2];
      const entityId = datasetId || match[1];
      return {
        id: entityId,
        entityType: WorkspaceEntityRouteType.DATASET,
        name: datasetId ? `Dataset #${datasetId}` : `Dataset Group #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.REPORT: {
      const match = matchers[WorkspaceEntityRouteType.REPORT]!;
      const reportId = match[2];
      const entityId = reportId || match[1];
      return {
        id: entityId,
        entityType: WorkspaceEntityRouteType.REPORT,
        name: reportId ? `Report #${reportId}` : `Blueprint #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.PROMPT: {
      const match = matchers[WorkspaceEntityRouteType.PROMPT]!;
      const promptName = options.promptNamesById?.[match[1]];
      const versionNumber = match[2];
      const name = promptName || `Prompt #${match[1]}`;
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.PROMPT,
        name: versionNumber ? `${name} v${versionNumber}` : name,
        shouldPreserveUrlMetadata: false,
      };
    }
    case WorkspaceEntityRouteType.WORKFLOW: {
      const match = matchers[WorkspaceEntityRouteType.WORKFLOW]!;
      const versionNumber = match[2];
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.WORKFLOW,
        name: versionNumber ? `Workflow #${match[1]} v${versionNumber}` : `Workflow #${match[1]}`,
        shouldPreserveUrlMetadata: false,
      };
    }
    case WorkspaceEntityRouteType.AB_TEST: {
      const match = matchers[WorkspaceEntityRouteType.AB_TEST]!;
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.AB_TEST,
        name: `AB Test #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.SNIPPET: {
      const match = matchers[WorkspaceEntityRouteType.SNIPPET]!;
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.SNIPPET,
        name: `Snippet #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.INPUT_VARIABLE_SET: {
      const match = matchers[WorkspaceEntityRouteType.INPUT_VARIABLE_SET]!;
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.INPUT_VARIABLE_SET,
        name: `Input Variable Set #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.SKILL_COLLECTION: {
      const match = matchers[WorkspaceEntityRouteType.SKILL_COLLECTION]!;
      const versionNumber = match[2];
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.SKILL_COLLECTION,
        name: versionNumber ? `Skill Collection v${versionNumber}` : "Skill Collection",
      };
    }
    case WorkspaceEntityRouteType.SMART_TABLE: {
      const match = matchers[WorkspaceEntityRouteType.SMART_TABLE]!;
      const tableName =
        options.smartTableNamesById?.[getSmartTableNameKey(workspaceId, match[1])] ??
        options.smartTableNamesById?.[match[1]];
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.SMART_TABLE,
        name: tableName || `Table #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.TOOL: {
      const match = matchers[WorkspaceEntityRouteType.TOOL]!;
      const toolName =
        options.toolNamesById?.[getToolNameKey(workspaceId, match[1])] ??
        options.toolNamesById?.[match[1]];
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.TOOL,
        name: toolName || `Tool #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.REQUEST: {
      const match = matchers[WorkspaceEntityRouteType.REQUEST]!;
      return {
        id: match[1],
        entityType: WorkspaceEntityRouteType.REQUEST,
        name: `Request #${match[1]}`,
      };
    }
    case WorkspaceEntityRouteType.TRACE: {
      const match = matchers[WorkspaceEntityRouteType.TRACE]!;
      const traceId = match[1];
      const shortTraceId =
        traceId.length > TRACE_ID_SHORT_DISPLAY_LENGTH
          ? `${traceId.slice(0, TRACE_ID_SHORT_DISPLAY_LENGTH)}…`
          : traceId;
      return {
        id: traceId,
        entityType: WorkspaceEntityRouteType.TRACE,
        name: `Trace ${shortTraceId}`,
      };
    }
    case WorkspaceEntityRouteType.EVALUATE_COMPARE: {
      const reportId = searchParams?.get("report_id");
      if (!reportId || !/^\d+$/.test(reportId)) {
        return null;
      }
      return {
        id: reportId,
        entityType: WorkspaceEntityRouteType.REPORT,
        name: `Report #${reportId}`,
      };
    }
    default:
      return null;
  }
};

const buildWorkspaceTagLink = ({
  workspaceId,
  resourcePath,
  parsedUrl,
  tagInfo,
}: {
  workspaceId: string;
  resourcePath: string;
  parsedUrl: URL;
  tagInfo: WorkspaceEntityTagInfo;
}) => {
  const baseLink = `/workspace/${workspaceId}/${resourcePath}`;
  if (tagInfo.shouldPreserveUrlMetadata === false) {
    return baseLink;
  }
  return `${baseLink}${parsedUrl.search}${parsedUrl.hash}`;
};

export const convertWorkspaceUrlsToTags = (
  message: string,
  options: ConvertWorkspaceUrlsToTagsOptions = {},
): string => {
  if (!message.trim()) {
    return message;
  }

  const tagTokenRanges = getTagTokenRanges(message);

  return message.replace(WORKSPACE_URL_REGEX, (rawMatch, offset: number) => {
    if (isInsideTagToken(offset, tagTokenRanges)) {
      return rawMatch;
    }

    const { core, trailing } = trimTrailingPunctuation(rawMatch);
    if (!core) {
      return rawMatch;
    }

    try {
      const normalized = core.startsWith("workspace/") ? `/${core}` : core;
      const parsedUrl =
        normalized.startsWith("http://") || normalized.startsWith("https://")
          ? new URL(normalized)
          : new URL(normalized, "http://localhost");

      const workspaceMatch = parsedUrl.pathname.match(/^\/workspace\/(\d+)\/(.+)$/);
      if (!workspaceMatch) {
        return rawMatch;
      }

      const workspaceId = workspaceMatch[1];
      const resourcePath = workspaceMatch[2];
      const tagInfo = parseWorkspaceEntityTagInfo(
        workspaceId,
        resourcePath,
        parsedUrl.searchParams,
        options,
      );
      if (!tagInfo) {
        return rawMatch;
      }

      const link = buildWorkspaceTagLink({ workspaceId, resourcePath, parsedUrl, tagInfo });
      const tag = `@@@tag:entity:${tagInfo.id}:${tagInfo.name}::${tagInfo.entityType}:${link}@@@`;
      return `${tag}${trailing}`;
    } catch {
      return rawMatch;
    }
  });
};

export const convertWorkspaceUrlsToTagsAsync = async (
  message: string,
  options: ConvertWorkspaceUrlsToTagsAsyncOptions = {},
): Promise<string> => {
  if (!message.trim()) {
    return message;
  }

  const { promptIds, smartTableRefs, toolRefs } = getWorkspaceEntityRefsFromWorkspaceUrls(message);
  if (promptIds.length === 0 && smartTableRefs.length === 0 && toolRefs.length === 0) {
    return convertWorkspaceUrlsToTags(message, options);
  }

  try {
    const [promptNamesById, smartTableNamesById, toolNamesById] = await Promise.all([
      options.resolvePromptNames
        ? options.resolvePromptNames(promptIds)
        : fetchPromptNamesById(promptIds, options.userToken),
      options.resolveSmartTableNames
        ? options.resolveSmartTableNames(smartTableRefs)
        : fetchSmartTableNamesById(smartTableRefs, options.userToken),
      options.resolveToolNames
        ? options.resolveToolNames(toolRefs)
        : fetchToolNamesById(toolRefs, options.userToken),
    ]);

    return convertWorkspaceUrlsToTags(message, {
      ...options,
      promptNamesById: {
        ...options.promptNamesById,
        ...promptNamesById,
      },
      smartTableNamesById: {
        ...options.smartTableNamesById,
        ...smartTableNamesById,
      },
      toolNamesById: {
        ...options.toolNamesById,
        ...toolNamesById,
      },
    });
  } catch {
    return convertWorkspaceUrlsToTags(message, options);
  }
};
